/*
	Deep clone function. Lossy.
	Do not use if object uses Dates, functions, undefined, Infinity, RegExps, Maps, Sets, Blobs, FileLists,
	ImageData, sparse Arrays, Typed Arrays or other complex types within your object.
 */
export function objectClone(obj: object) {
	return JSON.parse(JSON.stringify(obj));
}

export function isEmptyObject(obj = {}) {
	if (obj === null) {
		return false;
	}
	return (Object.keys(obj).length === 0 && obj.constructor === Object);
}

export function isObject(obj: any) { return obj === Object(obj); }


export function isPlainObject(value) {
	return Object.prototype.toString.call(value) === "[object Object]";
}

export const isBoolean = (val: any): val is boolean => typeof val === 'boolean';

export const isNumber = (val: any): val is number => typeof val === 'number';

export const isString = (val: unknown): val is string => typeof val === 'string';

export function isFunction(functionToCheck: any) {
	return !!(functionToCheck && {}.toString.call(functionToCheck) === '[object Function]');
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = () => {};


export function arrayRemoveItemAtIndex (array: any[], index: number) {
	return [
		...array.slice(0, index),
		...array.slice(index + 1)
	];
}

export function arrayInsertItemAtIndex (array: any[], index: number, item: any) {
	return [
		...array.slice(0, index),
		item,
		...array.slice(index)
	];
}

export function findElementWithIdInArray(array: any[], id: string|number, idProperty = 'id') {
	if (Array.isArray(array)) {
		return array.find(function(item) {
			return (item[idProperty] == id);
		});
	}

	return null;
}

export function findIndexOfElementWithIdInArray(array: any[], id: string|number, idProperty = 'id'): number {
	const found = findElementWithIdInArray(array, id, idProperty);
	if (found !== null) {
		const foundIndex = array.indexOf(found);
		if (foundIndex !== -1) {
			return foundIndex;
		}
	}
	return null;
}

export function findElementWithIdInAssocArray(array: {}, id: string|number) {
	let toReturn = null;

	Object.keys(array).map(function(key) {
		//console.log(id, key, (key == id), array[key]);
		if (key == id) {
			toReturn = array[key];
		}
	});

	return toReturn;
}

export function filterAssocArray(array: {[key: string]: any}, callbackFn: (item: any, key: string) => boolean): {[key: string]: any} {
	let toReturn = {};

	for (const [key, value] of Object.entries(array)) {
		if (callbackFn(value, key)) {
			// console.log('same', key, valueOne, valueTwo);
			toReturn[key] = value;
		}
	}

	return toReturn;
}

export function arrayUnique(array = []): string[] {
	return [...new Set(array)];
}

export function getByteSize(bytes?: number, numberOfTenths: number = 1): string {
	if (null == bytes) {
		return null;
	}

	if (bytes === 0) {
		return '0 bytes';
	}

	let toReturn = '';

	if (bytes < 0) {
		bytes *= -1;
		toReturn = '-';
	}

	if (bytes === 1) {
		return toReturn + '1 byte';
	} else if (bytes < 1024) {
		return toReturn + bytes + ' bytes';
	} else if (bytes < (1024 * 1024)) {
		return toReturn + (bytes / 1024).toFixed(numberOfTenths) + ' kb';
	} else if (bytes < (1024 * 1024 * 1024)) {
		return toReturn + (bytes / (1024 * 1024)).toFixed(numberOfTenths + 1) + ' mb';
	}

	return toReturn + (bytes / (1024 * 1024 * 1024)).toFixed(numberOfTenths + 1) + ' gb';
}

export function numberWithCommas(number: number) {
	if (number) {
		return number.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
	}
	return null;
}

export function isUrlRelative(url: string) {
	const r = new RegExp('^(?:[a-z+]+:)?//', 'i');
	return !r.test(url);
}

export function stripTrailingSlash(str: string) {
	return str.endsWith('/') ? str.slice(0, -1) : str;
}

export function strip_tags(str: string, allow: string = '') {
	// making sure allow arg is a string containing only tags in lowercase (<a><b><c>)
	allow = (((allow || "") + "").toLowerCase().match(/<[a-z][a-z0-9]*>/g) || []).join('');
	const tags = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi;
	const commentsAndPhpTags = /<!--[\s\S]*?-->|<\?(?:php)?[\s\S]*?\?>/gi;
	return str.replace(commentsAndPhpTags, '').replace(tags, function ($0: any, $1: string) {
		return allow.indexOf('<' + $1.toLowerCase() + '>') > -1 ? $0 : '';
	});
}

export function decodeHTMLEntities(text: string) {
	const entities = [['amp','&'], ['apos','\''], ['#x27','\''], ['#x2F','/'], ['#39','\''], ['#47','/'], ['lt','<'], ['gt','>'], ['nbsp',' '], ['quot','"']];

	for (let i = 0, max = entities.length; i < max; ++i)
		text = text.replace(new RegExp('&' + entities[i][0] + ';', 'g'), entities[i][1]);

	return text;
}

export function isJSON(input: string) {
	try {
		return JSON.parse(input) && !!input;
	} catch (e) {
		return false;
	}
}

export function getCookie(name) {
	const re = new RegExp(name + "=([^;]+)");
	const value = re.exec(document.cookie);
	return (value != null) ? unescape(value[1]) : null;
}

export function objectFlip(object: object) {
	const toReturn = {};
	Object.keys(object).forEach(key => {
		toReturn[object[key]] = key;
	});
	return toReturn;
}

export function slugify(input: string): string {
	if (!input) {
		return '';
	}

	// make lower case and trim
	let slug = input.toLowerCase().trim();

	// remove accents from chars
	slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

	// replace invalid chars with spaces
	slug = slug.replace(/[^a-z0-9\s-]/g, ' ').trim();

	// replace multiple spaces or hyphens with a single hyphen
	slug = slug.replace(/[\s-]+/g, '-');

	return slug;
}

function scrollTo(x = null, y = null, scrollOptions: ScrollOptions = { behavior: 'smooth' }) {
	if (typeof window !== 'undefined') {
		const s: ScrollToOptions = scrollOptions;
		if (typeof x === 'number') { s.left = x; }
		if (typeof y === 'number') { s.top = y; }
		window.scrollTo(s);
	}
}
